import React, { useState, useEffect, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Col, Row, Card, CardBody, CardHeader, Button } from "reactstrap";
import { Fn_AddEditData, Fn_DisplayData } from "../../store/Functions";
import { useDispatch } from "react-redux";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useLocation, useNavigate } from "react-router-dom";
import { Save, ArrowLeft, Truck, Settings, Sliders } from "react-feather";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const AddEdit_VehicleTypeMaster = () => {
  const API_URL_SAVE = "VehicleTypeMaster/0/token";
  const API_URL_EDIT = API_WEB_URLS.MASTER + "/0/token/VehicleType/Id";
  
  const breadCrumbTitle = "Vehicle Type Master";
  const breadcrumbItem = "Vehicle Type Master";

  const [state, setState] = useState({
    id: 0,
    formData: {
      Name: "",
      RangeFrom: "",
      RangeTo: "",
    },
  });

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const nameRef = useRef(null);

  useEffect(() => {
    const Id = (location.state && location.state.Id) || 0;

    if (Id > 0) {
      setState((prevState) => ({ ...prevState, id: Id }));
      Fn_DisplayData(dispatch, setState, Id, API_URL_EDIT);
    }
  }, [dispatch, location.state]);

  // Auto-focus on Name field when component mounts
  useEffect(() => {
    if (nameRef.current) {
      nameRef.current.focus();
    }
  }, []);

  // Validation Schema
  const validationSchema = Yup.object({
    Name: Yup.string()
      .required("Vehicle type name is required")
      .min(2, "Vehicle type name must be at least 2 characters")
      .max(100, "Vehicle type name must be less than 100 characters"),
    RangeFrom: Yup.number()
      .typeError("Range from must be a valid number")
      .required("Range from is required")
      .min(0, "Range from cannot be negative"),
    RangeTo: Yup.number()
      .typeError("Range to must be a valid number")
      .required("Range to is required")
      .min(Yup.ref("RangeFrom"), "Range to must be greater than or equal to range from"),
  });

  const handleSubmit = async (values) => {
    const obj = JSON.parse(localStorage.getItem("user"));
    let formData = new FormData();
    formData.append("Name", values.Name || "");
    formData.append("RangeFrom", values.RangeFrom ?? 0);
    formData.append("RangeTo", values.RangeTo ?? 0);
    formData.append("UserId", obj === null || obj === undefined ? 0 : obj.uid);

    Fn_AddEditData(
      dispatch,
      setState,
      { arguList: { id: state.id, formData } },
      API_URL_SAVE,
      true,
      "vehicletypeid",
      navigate,
      "/VehicleTypeMaster"
    );
  };

  return (
    <div className="master-form-container" style={{padding: '10px 0'}}>
      <div className="container-fluid px-2">
        <Breadcrumbs title={breadCrumbTitle} breadcrumbItem={breadcrumbItem} />
        
        {/* Form Card */}
        <Row className="justify-content-center">
          <Col lg={8} md={12} sm={12}>
            <Card className="master-form-card master-form-slide-in">
              <CardHeader className="master-form-card-header" style={{padding: '12px 20px'}}>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <div className="form-icon-wrapper me-2">
                      <Settings size={18} color="#6D68CB" />
                    </div>
                    <div>
                      <h5 className="mb-0 fw-bold">Vehicle Type Master</h5>
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardBody className="master-form-card-body">
                <Formik 
                  initialValues={{
                    Name: state.formData?.Name ?? "",
                    RangeFrom: state.formData?.RangeFrom ?? "",
                    RangeTo: state.formData?.RangeTo ?? "",
                  }} 
                  validationSchema={validationSchema} 
                  onSubmit={handleSubmit}
                  enableReinitialize={true}
                >
                  {({ isSubmitting, errors, touched }) => (
                    <Form>
                      <div className="form-group mb-3">
                        <label htmlFor="Name" className="master-form-label">
                          <Truck size={16} className="me-2" />
                          Vehicle Type Name <span className="required">*</span>
                        </label>
                        <div className="input-wrapper">
                          <Field
                            innerRef={nameRef}
                            className={`master-form-input ${errors.Name && touched.Name ? 'is-invalid' : ''}`}
                            type="text"
                            name="Name"
                            placeholder="Enter vehicle type name (e.g., Car, Truck, Van)"
                          />
                          <div className="input-icon">
                            <Truck size={16} color="#6c757d" />
                          </div>
                        </div>
                        <ErrorMessage name="Name" component="div" className="master-form-error" />
                        <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                          <i className="fas fa-lightbulb me-1"></i>
                          Enter the type of vehicle (e.g., Car, Motorcycle, Truck, Bus, Van)
                        </div>
                      </div>

                      <Row className="g-3">
                        <Col md={6}>
                          <div className="form-group mb-3 mb-md-0">
                            <label htmlFor="RangeFrom" className="master-form-label">
                              <Sliders size={16} className="me-2" />
                              Range From <span className="required">*</span>
                            </label>
                            <div className="input-wrapper">
                              <Field
                                className={`master-form-input ${errors.RangeFrom && touched.RangeFrom ? 'is-invalid' : ''}`}
                                type="number"
                                name="RangeFrom"
                                placeholder="Enter starting range"
                                min="0"
                              />
                              <div className="input-icon">
                                <Sliders size={16} color="#6c757d" />
                              </div>
                            </div>
                            <ErrorMessage name="RangeFrom" component="div" className="master-form-error" />
                            <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                              <i className="fas fa-lightbulb me-1"></i>
                              Specify the lower limit for this vehicle type.
                            </div>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="form-group">
                            <label htmlFor="RangeTo" className="master-form-label">
                              <Sliders size={16} className="me-2" />
                              Range To <span className="required">*</span>
                            </label>
                            <div className="input-wrapper">
                              <Field
                                className={`master-form-input ${errors.RangeTo && touched.RangeTo ? 'is-invalid' : ''}`}
                                type="number"
                                name="RangeTo"
                                placeholder="Enter ending range"
                                min="0"
                              />
                              <div className="input-icon">
                                <Sliders size={16} color="#6c757d" />
                              </div>
                            </div>
                            <ErrorMessage name="RangeTo" component="div" className="master-form-error" />
                            <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                              <i className="fas fa-lightbulb me-1"></i>
                              Ensure this value is greater than or equal to the starting range.
                            </div>
                          </div>
                        </Col>
                      </Row>

                      <div className="master-form-actions mt-4">
                        <div className="d-flex gap-2 justify-content-center">
                          <Button
                            type="button"
                            className="master-form-cancel-btn"
                            onClick={() => navigate("/VehicleTypeMaster")}
                            variant="outline-secondary"
                            size="md"
                          >
                            <ArrowLeft size={16} className="me-1" />
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            className="master-form-submit-btn"
                            disabled={isSubmitting}
                            size="md"
                          >
                            {isSubmitting ? (
                              <div className="master-form-loading me-1" />
                            ) : (
                              <Save size={16} className="me-1" />
                            )}
                            {state.id > 0 ? 'Update Vehicle Type' : 'Save Vehicle Type'}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default AddEdit_VehicleTypeMaster;

